import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";

import {
  Divider as MuiDivider,
  Grid,
  Typography as MuiTypography,
  ToggleButtonGroup,
  ToggleButton as MuiToggleButton,
} from "@mui/material";
import { spacing, SpacingProps } from "@mui/system";
import {
  TableRows as TableIcon,
  TableChart as DefaultIcon,
} from "@mui/icons-material";

import { RootState } from "../../redux/store";
import { setBookmarkColorMapping } from "../../redux/slices/bookmark";
import NoResultsFound from "../../components/pages/NoResultsFound";
import BookmarkCard from "../../components/pages/bookmark/BookmarkCard";
import BookmarkGroup from "../../components/pages/bookmark/BookmarkGroup";
import { Bookmark } from "../../types/app";
import { getColor } from "../../utils/clusterColor";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";

const Divider = styled(MuiDivider)(spacing);

interface TypographyProps extends SpacingProps {
  component?: string;
}

const Typography = styled(MuiTypography)<TypographyProps>(spacing);

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MainContainer = styled.div`
  padding-bottom: 20px;
`;

const ToggleButton = styled(MuiToggleButton)`
  &.MuiToggleButton-root.Mui-selected {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: white;
  }
`;

function BookmarkPage() {
  const dispatch = useAppDispatch();
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const bookmarkResult = bookmarkState.bookmarks;
  const [displayMethod, setDisplayMethod] = useState("default");
  const groups: Record<string, Bookmark[]> = {};
  bookmarkResult.forEach((bookmark: Bookmark) => {
    if (groups[bookmark.group]) {
      groups[bookmark.group].push(bookmark);
    } else {
      groups[bookmark.group] = [bookmark];
    }
  });

  useEffect(() => {
    const colorMapping: Record<string, string> = {};
    Object.keys(groups).forEach((group: string, idx) => {
      colorMapping[group] = getColor(Object.keys(groups).length, idx, false);
    });
    dispatch(setBookmarkColorMapping(colorMapping));
  }, [bookmarkState.bookmarks]);

  return (
    <React.Fragment>
      {bookmarkResult.length === 0 ? (
        <NoResultsFound />
      ) : (
        <MainContainer>
          <TopSection>
            <Typography variant="h3" gutterBottom display="inline">
              Bookmarks
            </Typography>
            <ToggleButtonGroup
              value={displayMethod}
              exclusive
              onChange={(e, value) => setDisplayMethod(value)}
            >
              <ToggleButton value="default">
                <DefaultIcon sx={{ fontSize: "17px" }} />
              </ToggleButton>
              <ToggleButton value="list">
                <TableIcon sx={{ fontSize: "17px" }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </TopSection>

          <Divider my={6} />

          <Grid container spacing={6}>
            {displayMethod === "default"
              ? Object.keys(groups).map((group: string) => {
                  return (
                    <Grid item xs={12} lg={6} xl={4} key={group}>
                      <BookmarkGroup
                        groupName={group}
                        bookmarks={groups[group]}
                      />
                    </Grid>
                  );
                })
              : bookmarkResult.map((bookmark: Bookmark) => {
                  return (
                    <Grid item xs={12} lg={6} xl={4} key={bookmark.index}>
                      <BookmarkCard bookmark={bookmark} />
                    </Grid>
                  );
                })}
          </Grid>
        </MainContainer>
      )}
    </React.Fragment>
  );
}

export default BookmarkPage;
